import React from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';

const TRUTH_ROWS = [
  { trusted: false, invite: false, lock: false, trustedIp: 'wait', token: 'wait' },
  { trusted: false, invite: false, lock: true, trustedIp: 'wait', token: 'wait' },
  { trusted: false, invite: true, lock: false, trustedIp: 'wait', token: 'now' },
  { trusted: false, invite: true, lock: true, trustedIp: 'wait', token: 'now' },
  { trusted: true, invite: false, lock: false, trustedIp: 'now', token: 'wait' },
  { trusted: true, invite: false, lock: true, trustedIp: 'wait', token: 'wait' },
  { trusted: true, invite: true, lock: false, trustedIp: 'now', token: 'now' },
  { trusted: true, invite: true, lock: true, trustedIp: 'wait', token: 'now' },
];

function matchesSettings(row, settings) {
  return row.trusted === !!settings.enabled
    && row.invite === !!settings.autoApproveWithInvitation
    && row.lock === !!settings.requireAdminApproval;
}

function OnOff({ on }) {
  return (
    <Chip
      size="small"
      label={on ? 'On' : 'Off'}
      color={on ? 'primary' : 'default'}
      variant={on ? 'filled' : 'outlined'}
    />
  );
}

function Outcome({ kind }) {
  const approved = kind === 'now';
  return (
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: approved ? 'success.dark' : 'text.secondary' }}
    >
      {approved ? 'Joins unclaimed' : 'Waits for you'}
    </Typography>
  );
}

function RegistrationSequence() {
  const actors = ['Gateway', 'Hub', 'Signed-in user'];
  const messages = [
    { from: 0, to: 1, text: 'Self-register. No account yet.' },
    { from: 1, to: 0, text: 'If approved: device token, still unclaimed' },
    { from: 2, to: 1, text: 'Claim that device token' },
    { from: 1, to: 2, text: 'Gateway is now on this account' },
  ];
  const width = 680;
  const actorX = [110, 340, 570];
  const firstMessage = 78;
  const gap = 58;
  const height = firstMessage + messages.length * gap + 24;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: '100%', minWidth: 560, height: 'auto' }} role="img" aria-label="A gateway self-registers, the hub returns a device token, and a signed-in user claims it">
        {actors.map((label, index) => (
          <g key={label}>
            <rect x={actorX[index] - 72} y={4} width={144} height={28} rx={6} fill="#1976d2" />
            <text x={actorX[index]} y={23} textAnchor="middle" fill="#fff" fontSize="13" fontFamily="Roboto, Arial, sans-serif">
              {label}
            </text>
            <line x1={actorX[index]} y1={32} x2={actorX[index]} y2={height - 8} stroke="#90caf9" strokeDasharray="4 4" />
          </g>
        ))}
        {messages.map((message, index) => {
          const y = firstMessage + index * gap;
          const x1 = actorX[message.from];
          const x2 = actorX[message.to];
          const left = Math.min(x1, x2);
          const right = Math.max(x1, x2);
          const pointsRight = x2 > x1;
          const lineEnd = pointsRight ? x2 - 8 : x2 + 8;
          return (
            <g key={message.text}>
              <line x1={x1} y1={y} x2={lineEnd} y2={y} stroke="#424242" strokeWidth="1.5" />
              <polygon
                points={pointsRight ? `${x2},${y} ${x2 - 8},${y - 4} ${x2 - 8},${y + 4}` : `${x2},${y} ${x2 + 8},${y - 4} ${x2 + 8},${y + 4}`}
                fill="#424242"
              />
              <text
                x={(left + right) / 2}
                y={y - 8}
                textAnchor="middle"
                fill="#212121"
                fontSize="12"
                fontFamily="Roboto, Arial, sans-serif"
              >
                {message.text}
              </text>
            </g>
          );
        })}
      </Box>
    </Box>
  );
}

const AutoApprovalGuideDialog = ({ open, onClose, settings }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>How auto-approval works</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body1" sx={{ mb: 2 }}>
        A gateway registers itself. It does not belong to anyone yet. These switches only decide whether that registration joins the hub immediately or waits in Pending Requests. Joining still leaves it unclaimed. A signed-in user claims the device token the hub created for that gateway, and that claim is what maps it to their account.
      </Typography>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Truth table
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Your current settings are highlighted. A trusted IP is an address on the whitelist or inside a network range. “Joins unclaimed” means the gateway is on the hub with no owner. “Waits for you” means it stays in Pending Requests until an admin approves it. Neither outcome assigns a user.
      </Typography>
      <TableContainer sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Trusted networks</TableCell>
              <TableCell>Invitation tokens</TableCell>
              <TableCell>Require admin for whitelist</TableCell>
              <TableCell>Trusted IP, no token</TableCell>
              <TableCell>Presents an invitation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {TRUTH_ROWS.map((row) => {
              const current = matchesSettings(row, settings);
              return (
                <TableRow
                  key={`${row.trusted}-${row.invite}-${row.lock}`}
                  selected={current}
                  sx={current ? { bgcolor: 'action.selected' } : undefined}
                >
                  <TableCell><OnOff on={row.trusted} /></TableCell>
                  <TableCell><OnOff on={row.invite} /></TableCell>
                  <TableCell><OnOff on={row.lock} /></TableCell>
                  <TableCell><Outcome kind={row.trustedIp} /></TableCell>
                  <TableCell><Outcome kind={row.token} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Two different tokens
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        The device token is what the hub mints when a registration is approved. Every approved gateway gets one, including one that showed up on its own. It does not mean the gateway is trusted, and it does not assign an owner. The person installing the gateway, or an admin, pastes that device token into Claim Gateway while signed in. That claim is the step that maps the gateway to a user account.
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        An invitation token is optional and separate. A hub admin creates one ahead of time for a known installer, with a use limit and an expiry. The hub does not mint an invitation for an untrusted gateway that self-registers. The installer can present that code during registration. If the invitation switch is on, and the code is still active, the registration can skip the pending queue. The gateway is still unclaimed until someone claims its device token.
      </Typography>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Registration sequence
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        This is the path a gateway takes today. The highlighted row decides the second arrow: join immediately, or wait until an admin approves it. An invitation is not part of this path unless an admin already created one and the installer presented it.
      </Typography>
      <RegistrationSequence />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

export default AutoApprovalGuideDialog;
